// Editing a route: a working copy as a list of waypoints, each followed by the leg of hidden points (usage 0)
// that shape the line to the next waypoint (a snapped path). Every change is a pure function returning a new
// copy, so undo is just keeping the old ones (see History). A change to a waypoint straightens the legs either
// side of it and marks them pending; snapping then fills each one in, or leaves it straight (resolveLeg).
// Saving stores the whole route, so removed points simply go.
import type { ApiPoint, ApiTrail, LonLat } from './trail';
import { isPoi, isWaypoint } from './trail';
import type { PoiDetails } from './poi';
import { poiPoint, routePois, withPoiExtension } from './poi';

export interface EditWaypoint {
  point: ApiPoint;
  /** Hidden points from this waypoint to the next (empty for a straight line, and for the finish). */
  leg: ApiPoint[];
  /** The leg has changed and is waiting to be snapped (or left straight). */
  pending?: boolean;
}

export interface RouteEdit {
  waypoints: EditWaypoint[];
  /** Points of interest on the route, kept as they are. */
  pois: ApiPoint[];
}

const ROUTE_POINT = 1;
const SHOWN = 1;
const HIDDEN = 0;

export const emptyRoute = (): RouteEdit => ({ waypoints: [], pois: [] });

const newPoint = ([longitude, latitude]: LonLat, usage: number): ApiPoint => ({
  latitude,
  longitude,
  pointType: ROUTE_POINT,
  usage,
});

/** The same point without its id, for a new route made from part of another. */
const fresh = (p: ApiPoint): ApiPoint => ({ ...p, guid: undefined });

/** Straightens the leg after waypoint i (if it isn't the finish) and marks it for snapping. */
function touch(waypoints: EditWaypoint[], i: number) {
  const w = waypoints[i];
  if (w && i < waypoints.length - 1) waypoints[i] = { ...w, leg: [], pending: true };
}

const samePosition = (p: ApiPoint, [lon, lat]: LonLat) => p.longitude === lon && p.latitude === lat;

/** A waypoint copied into a new route: no ids, still waiting to be snapped if it was. */
const freshWaypoint = (w: EditWaypoint): EditWaypoint => ({
  ...w,
  point: fresh(w.point),
  leg: w.leg.map(fresh),
});

export function fromApi(data: ApiTrail): RouteEdit {
  const points = (data.points ?? []).filter((p) => !p.deleted);
  const line = points.filter((p) => !isPoi(p));
  const waypoints: EditWaypoint[] = [];
  line.forEach((p, i) => {
    if (i === 0 || i === line.length - 1 || isWaypoint(p)) waypoints.push({ point: p, leg: [] });
    else waypoints.at(-1)!.leg.push(p);
  });
  return { waypoints, pois: routePois({ ...data, points }) };
}

/** The route as it is saved: the line as its points and the points of interest in its xgoPois extension (poi.ts). */
export function toApiRoute(base: ApiTrail, edit: RouteEdit): ApiTrail {
  const line = toApiPoints({ ...edit, pois: [] });
  return { ...base, points: line, extensions: withPoiExtension(base.extensions, edit.pois) };
}

/** The route's points as the API takes them: the line in order, then POIs. */
export function toApiPoints(edit: RouteEdit): ApiPoint[] {
  const line = edit.waypoints.flatMap((w) => [{ ...w.point, usage: SHOWN }, ...w.leg]);
  return [...line, ...edit.pois];
}

/** Every position on the line, in order. */
export const lineOf = (edit: RouteEdit): LonLat[] =>
  edit.waypoints.flatMap((w) => [w.point, ...w.leg]).map((p) => [p.longitude, p.latitude]);

/** The waypoints' positions (the handles to drag). */
export const waypointsOf = (edit: RouteEdit): LonLat[] =>
  edit.waypoints.map((w) => [w.point.longitude, w.point.latitude]);

/**
 * A handle on each stretch between waypoints i and i + 1, dragged to insert a waypoint there: halfway along a
 * straight stretch, or the middle point of a snapped one.
 */
export function midpointsOf(edit: RouteEdit): LonLat[] {
  return edit.waypoints.slice(0, -1).map((w, i) => {
    const next = edit.waypoints[i + 1]!.point;
    const p = w.leg[Math.floor(w.leg.length / 2)];
    if (p) return [p.longitude, p.latitude];
    return [(w.point.longitude + next.longitude) / 2, (w.point.latitude + next.latitude) / 2];
  });
}

export function appendWaypoint(edit: RouteEdit, position: LonLat): RouteEdit {
  const waypoints = [...edit.waypoints, { point: newPoint(position, SHOWN), leg: [] }];
  touch(waypoints, waypoints.length - 2);
  return { ...edit, waypoints };
}

/** Adds a point of interest beside the route (see poi.ts for how it is stored). */
export function addPoi(edit: RouteEdit, position: LonLat, details: PoiDetails): RouteEdit {
  return { ...edit, pois: [...edit.pois, poiPoint(position, details)] };
}

/** Changes the details of the route's point of interest i, keeping its place and id. */
export function updatePoi(edit: RouteEdit, i: number, details: PoiDetails): RouteEdit {
  const old = edit.pois[i];
  if (!old) return edit;
  const pois = [...edit.pois];
  pois[i] = poiPoint([old.longitude, old.latitude], details, old);
  return { ...edit, pois };
}

/** Moves the route's point of interest i. */
export function movePoi(edit: RouteEdit, i: number, [longitude, latitude]: LonLat): RouteEdit {
  const old = edit.pois[i];
  if (!old) return edit;
  const pois = [...edit.pois];
  pois[i] = { ...old, longitude, latitude };
  return { ...edit, pois };
}

export function removePoi(edit: RouteEdit, i: number): RouteEdit {
  return { ...edit, pois: edit.pois.filter((_, j) => j !== i) };
}

/** Puts a new waypoint between waypoints i and i + 1 (dragging the middle of a segment). */
export function insertWaypoint(edit: RouteEdit, i: number, position: LonLat): RouteEdit {
  if (i < 0 || i >= edit.waypoints.length - 1) return edit;
  const waypoints = [...edit.waypoints];
  waypoints.splice(i + 1, 0, { point: newPoint(position, SHOWN), leg: [] });
  touch(waypoints, i);
  touch(waypoints, i + 1);
  return { ...edit, waypoints };
}

export function moveWaypoint(edit: RouteEdit, i: number, position: LonLat): RouteEdit {
  const w = edit.waypoints[i];
  if (!w) return edit;
  const waypoints = [...edit.waypoints];
  // The old height no longer applies.
  const point = { ...w.point, longitude: position[0], latitude: position[1], elevation: undefined };
  waypoints[i] = { ...w, point };
  touch(waypoints, i - 1);
  touch(waypoints, i);
  return { ...edit, waypoints };
}

export function deleteWaypoint(edit: RouteEdit, i: number): RouteEdit {
  if (!edit.waypoints[i]) return edit;
  const waypoints = [...edit.waypoints];
  waypoints.splice(i, 1);
  // Deleting the finish makes the waypoint before it the finish, with no leg.
  if (i === waypoints.length && i > 0) waypoints[i - 1] = { point: waypoints[i - 1]!.point, leg: [] };
  else touch(waypoints, i - 1);
  return { ...edit, waypoints };
}

/** Removes every waypoint after waypoint i. */
export function deleteAfter(edit: RouteEdit, i: number): RouteEdit {
  if (i < 0 || i >= edit.waypoints.length - 1) return edit;
  const waypoints = edit.waypoints.slice(0, i + 1);
  waypoints[i] = { point: waypoints[i]!.point, leg: [] }; // now the finish
  return { ...edit, waypoints };
}

/** Removes every waypoint before waypoint i. */
export function deleteBefore(edit: RouteEdit, i: number): RouteEdit {
  if (i <= 0 || i >= edit.waypoints.length) return edit;
  return { ...edit, waypoints: edit.waypoints.slice(i) };
}

/** The legs waiting to be snapped, by the positions of their two ends. */
export function pendingLegs(edit: RouteEdit): { from: LonLat; to: LonLat }[] {
  return edit.waypoints.flatMap((w, i) => {
    const next = edit.waypoints[i + 1];
    if (!w.pending || !next) return [];
    return [
      {
        from: [w.point.longitude, w.point.latitude] as LonLat,
        to: [next.point.longitude, next.point.latitude] as LonLat,
      },
    ];
  });
}

/**
 * Settles the pending leg from `from` to `to` (found by position, as the route may have changed while it was
 * being snapped): `path` is the snapped line including its two ends, which move the waypoints onto it; null
 * leaves the leg straight. Unchanged if there is no such pending leg any more.
 */
export function resolveLeg(edit: RouteEdit, from: LonLat, to: LonLat, path: LonLat[] | null): RouteEdit {
  const i = edit.waypoints.findIndex(
    (w, j) =>
      w.pending &&
      j < edit.waypoints.length - 1 &&
      samePosition(w.point, from) &&
      samePosition(edit.waypoints[j + 1]!.point, to),
  );
  if (i < 0) return edit;
  const waypoints = [...edit.waypoints];
  if (!path || path.length < 2) {
    waypoints[i] = { point: waypoints[i]!.point, leg: [] };
    return { ...edit, waypoints };
  }
  const onto = (p: ApiPoint, position: LonLat): ApiPoint =>
    samePosition(p, position)
      ? p
      : { ...p, longitude: position[0], latitude: position[1], elevation: undefined };
  waypoints[i] = {
    point: onto(waypoints[i]!.point, path[0]!),
    leg: path.slice(1, -1).map((p) => newPoint(p, HIDDEN)),
  };
  const next = waypoints[i + 1]!;
  waypoints[i + 1] = { ...next, point: onto(next.point, path.at(-1)!) };
  return { ...edit, waypoints };
}

/**
 * Fills in the heights of points that have none, from `heightAt` (position → metres, or undefined if unknown).
 * Unchanged (the same object) if there is nothing to fill.
 */
export function applyHeights(
  edit: RouteEdit,
  heightAt: (position: LonLat) => number | undefined,
  /** Replace heights the points already have (refreshing the profile), not just fill in missing ones. */
  replace = false,
): RouteEdit {
  let changed = false;
  const fill = (p: ApiPoint): ApiPoint => {
    if (!replace && typeof p.elevation === 'number') return p;
    const m = heightAt([p.longitude, p.latitude]);
    if (m === undefined || m === p.elevation) return p;
    changed = true;
    return { ...p, elevation: m };
  };
  const waypoints = edit.waypoints.map((w) => ({ ...w, point: fill(w.point), leg: w.leg.map(fill) }));
  return changed ? { ...edit, waypoints } : edit;
}

export function reverse(edit: RouteEdit): RouteEdit {
  const n = edit.waypoints.length;
  // Waypoint j of the reversed route is followed by the reversed leg that led into it.
  const waypoints = edit.waypoints.map((_, j) => {
    const before = edit.waypoints[n - 2 - j];
    return {
      point: edit.waypoints[n - 1 - j]!.point,
      leg: before ? [...before.leg].reverse() : [],
      pending: before?.pending,
    };
  });
  return { ...edit, waypoints };
}

/**
 * Splits at waypoint i: the route keeps the start up to that waypoint, and a new route runs from it to the
 * finish. Returns [kept, new].
 */
export function split(edit: RouteEdit, i: number): [RouteEdit, RouteEdit] | null {
  if (i <= 0 || i >= edit.waypoints.length - 1) return null;
  const rest = edit.waypoints.slice(i).map(freshWaypoint);
  return [deleteAfter(edit, i), { waypoints: rest, pois: [] }];
}

/** Adds other routes onto the end of this one, joined by straight lines. */
export function join(edit: RouteEdit, others: RouteEdit[]): RouteEdit {
  const waypoints = [...edit.waypoints, ...others.flatMap((o) => o.waypoints.map(freshWaypoint))];
  touch(waypoints, edit.waypoints.length - 1);
  return { ...edit, waypoints };
}

/** Undo and redo over a sequence of copies. */
export interface History<T> {
  past: T[];
  present: T;
  future: T[];
}

const HISTORY_LIMIT = 100;

export const startHistory = <T>(present: T): History<T> => ({ past: [], present, future: [] });

export function record<T>(history: History<T>, next: T): History<T> {
  if (next === history.present) return history;
  return { past: [...history.past, history.present].slice(-HISTORY_LIMIT), present: next, future: [] };
}

export function undo<T>(history: History<T>): History<T> {
  const previous = history.past.at(-1);
  if (previous === undefined) return history;
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future] };
}

export function redo<T>(history: History<T>): History<T> {
  const [next, ...future] = history.future;
  if (next === undefined) return history;
  return { past: [...history.past, history.present], present: next, future };
}
